<script setup lang="ts">
import { bindField, revealErrors } from '#core/utils/bind-field'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { PasswordInput } from '@forgd/ui-kit/blocks/password-input'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { CheckCircleIcon, InformationCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { z } from 'zod'

interface RegisterForm {
  email: string
  password: string
  passwordConfirm: string
}

const { registerData, setView } = useLoginModal()

const emailSchema = z.string().trim().email('Enter a valid email')

const schema = z.object({
  email: emailSchema,
  password: z.string(),
  passwordConfirm: z.string(),
})

const form = useForm({
  defaultValues: {
    email: registerData.email,
    password: registerData.password,
    passwordConfirm: registerData.password,
  } satisfies RegisterForm,
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  onSubmitInvalid: ({ formApi }) => revealErrors(formApi),
  validators: { onDynamic: schema },
  onSubmit: ({ value }) => onContinue(value),
})

const values = form.useSelector(s => s.values)

const rules = [
  {
    label: 'Passwords should match',
    check: () => values.value.password.length > 0 && values.value.password === values.value.passwordConfirm,
  },
  {
    label: '8-64 characters',
    check: () => values.value.password.length >= 8 && values.value.password.length <= 64,
  },
  {
    label: 'Combine Upper and lower case characters',
    check: () => /[a-z]/.test(values.value.password) && /[A-Z]/.test(values.value.password),
  },
  {
    label: 'Include number and special characters (!@#$%^&*)',
    check: () => /\d/.test(values.value.password) && /[!@#$%^&*]/.test(values.value.password),
  },
]

const showRuleState = computed(() => values.value.password.length > 0 && values.value.passwordConfirm.length > 0)
const emailValid = computed(() => emailSchema.safeParse(values.value.email).success)
const canContinue = computed(() => emailValid.value && rules.every(rule => rule.check()))

const checking = ref(false)
const emailExists = ref(false)

async function onContinue(value: RegisterForm) {
  if (!canContinue.value || checking.value) {
    return
  }
  checking.value = true
  emailExists.value = false

  const email = value.email.trim()
  try {
    const result = await apiFetch('/auth/check-email', {
      method: 'post',
      params: { body: { email } },
    })
    if (result?.exists) {
      emailExists.value = true
      return
    }
  }
  catch {
    // If the check fails, fall through — signUp at Finalize still guards duplicates.
  }
  finally {
    checking.value = false
  }

  registerData.email = email
  registerData.password = value.password
  setView('register-details')
}
</script>

<template>
  <div class="flex w-full flex-col gap-5">
    <div>
      <h1 class="text-lg font-semibold text-primal">
        Join Forgd for free
      </h1>
      <p class="text-sm text-secondary">
        Register to access extra features.
      </p>
    </div>

    <form class="space-y-3" novalidate @submit.prevent="form.handleSubmit()">
      <form.Field v-slot="{ field }" name="email">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Email
          </FieldLabel>
          <FieldContent>
            <Input
              v-bind="bindField(field).control"
              :name="field.name"
              :error="bindField(field).control.error || emailExists"
              type="text"
              size="md"
              placeholder="e.g., user@email.com"
              @update:model-value="emailExists = false"
            />
            <FieldError v-bind="bindField(field).error" />
            <div v-if="emailExists" class="text-sm text-error">
              <p class="font-semibold">
                It seems this email already exists in Forgd.
              </p>
              <p>
                If you are attempting to sign in, please click the button below, otherwise try a different email.
              </p>
            </div>
          </FieldContent>
        </Field>
      </form.Field>

      <form.Field v-slot="{ field }" name="password">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Password
          </FieldLabel>
          <FieldContent>
            <PasswordInput
              v-bind="bindField(field).control"
              :name="field.name"
              :disabled="emailExists"
              size="md"
              autocomplete="new-password"
              placeholder="Enter your password"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>

      <form.Field v-slot="{ field }" name="passwordConfirm">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Confirm password
          </FieldLabel>
          <FieldContent>
            <PasswordInput
              v-bind="bindField(field).control"
              :name="field.name"
              :disabled="emailExists"
              size="md"
              autocomplete="new-password"
              placeholder="Enter your password again"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>

      <!-- Live password rules -->
      <ul class="space-y-1.5 pt-1">
        <li
          v-for="rule in rules"
          :key="rule.label"
          class="flex items-center gap-2 text-sm text-secondary"
        >
          <InformationCircleIcon
            v-if="!showRuleState"
            class="size-4 shrink-0 text-accent"
            aria-hidden="true"
          />
          <CheckCircleIcon
            v-else-if="rule.check()"
            class="size-4 shrink-0 text-green-600"
            aria-hidden="true"
          />
          <XCircleIcon
            v-else
            class="size-4 shrink-0 text-error"
            aria-hidden="true"
          />
          {{ rule.label }}
        </li>
      </ul>

      <p class="text-sm text-secondary">
        By clicking “Continue”, you agree to Forgd’s
        <a href="/terms-conditions" target="_blank" class="font-semibold text-accent">Terms of Use</a>
        and
        <a href="/privacy-policy" target="_blank" class="font-semibold text-accent">privacy policy</a>.
      </p>

      <Button
        size="lg"
        class="h-12"
        block
        type="submit"
        :loading="checking"
        :disabled="!canContinue || emailExists"
      >
        Continue
      </Button>
    </form>

    <p class="text-sm text-gray-600">
      Already have an account?
      <button type="button" class="font-semibold text-accent" @click="setView('login')">
        Login
      </button>
    </p>
  </div>
</template>
